import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, RefreshCw, LogOut, Loader, ChevronDown, ChevronUp, CheckCircle, ArrowRight } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { authApi, AuthApiError } from '../services/authApi';

export const EmailVerificationPendingPage: React.FC = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [changeEmailError, setChangeEmailError] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [verified, setVerified] = useState(false);

  // Poll for verification status every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await refreshUser();
      } catch {
        // Ignore errors during polling
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [refreshUser]);

  // Detect when user becomes verified
  useEffect(() => {
    if (user?.emailVerified) {
      setVerified(true);
    }
  }, [user?.emailVerified]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = useCallback(async () => {
    if (resendCooldown > 0 || isResending) return;
    setIsResending(true);
    setResendMessage('');
    try {
      await authApi.resendVerification();
      setResendMessage('Verification email sent!');
      setResendCooldown(60);
    } catch (error) {
      if (error instanceof AuthApiError) {
        setResendMessage(error.message);
      } else {
        setResendMessage('Failed to send verification email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  }, [resendCooldown, isResending]);

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangeEmailError('');
    setIsChangingEmail(true);
    try {
      await authApi.changeEmail(newEmail, currentPassword);
      await refreshUser();
      setShowChangeEmail(false);
      setNewEmail('');
      setCurrentPassword('');
      setResendMessage('Email updated! A new verification link has been sent.');
      setResendCooldown(60);
    } catch (error) {
      if (error instanceof AuthApiError) {
        setChangeEmailError(error.message);
      } else {
        setChangeEmailError('Failed to change email. Please try again.');
      }
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  // Show verified state
  if (verified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-cyan-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <Logo size="lg" className="mx-auto mb-4" />
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>

            <h1 className="text-2xl font-bold text-medical-900 mb-4">
              Email Verified!
            </h1>

            <p className="text-medical-600 mb-6">
              Your email address has been successfully verified. You're all set.
            </p>

            <button
              onClick={handleContinue}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              <span>Continue to ClinLexis</span>
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto mb-4" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-medical-200 p-8 text-center">
          <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="h-12 w-12 text-cyan-600" />
          </div>

          <h1 className="text-2xl font-bold text-medical-900 mb-4">
            Check Your Email
          </h1>

          <p className="text-medical-600 mb-2">
            We've sent a verification link to
          </p>
          <p className="font-semibold text-medical-900 mb-6">
            {user?.email}
          </p>

          <p className="text-sm text-medical-500 mb-6">
            Please click the link in your email to verify your address and access ClinLexis. The link expires in 24 hours.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              Can't find it? Check your spam or junk folder.
            </p>
          </div>

          {resendMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-green-800">{resendMessage}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Resend in {resendCooldown}s</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  <span>Resend Verification Email</span>
                </>
              )}
            </button>

            <button
              onClick={handleContinue}
              className="w-full btn-secondary flex items-center justify-center space-x-2"
            >
              <span>I've verified my email</span>
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              onClick={() => setShowChangeEmail(!showChangeEmail)}
              className="w-full text-sm text-primary-600 hover:text-primary-800 transition-colors flex items-center justify-center space-x-1"
            >
              <span>Wrong email address?</span>
              {showChangeEmail ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>

          {showChangeEmail && (
            <form onSubmit={handleChangeEmail} className="mt-4 text-left space-y-3">
              <div>
                <label className="block text-sm font-medium text-medical-700 mb-1">
                  New email address
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  required
                  className="input-field"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-medical-700 mb-1">
                  Confirm your password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="input-field"
                  placeholder="Enter your password"
                />
              </div>
              {changeEmailError && (
                <p className="text-sm text-red-600">{changeEmailError}</p>
              )}
              <button
                type="submit"
                disabled={isChangingEmail || !newEmail || !currentPassword}
                className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isChangingEmail ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <span>Update Email &amp; Resend</span>
                )}
              </button>
            </form>
          )}
        </div>

        <div className="mt-4 text-center">
          <button
            onClick={() => logout()}
            className="text-sm text-medical-500 hover:text-medical-700 transition-colors inline-flex items-center space-x-1"
          >
            <LogOut className="h-3 w-3" />
            <span>Sign out</span>
          </button>
        </div>
      </div>
    </div>
  );
};
